import { useEffect, useRef, useState } from 'react';

import PostHog from '../lib/utils/posthog';

// Variants that gate a redirect can't be read optimistically: painting the
// control UI and swapping it out is the flicker the gate exists to prevent.
// Callers withhold that UI until `resolved`, so resolution is capped — a
// blocked /flags request degrades to control instead of stranding the page.
export const FLAG_TIMEOUT_MS = 1500;

const RESOLVED_CONTROL = { variant: null, resolved: true };
const PENDING = { variant: null, resolved: false };

// A null flag skips evaluation when the experiment's UI is not displayed.
export default function usePostHogExperiment(flag) {
  const [state, setState] = useState(() => {
    if (!flag) return RESOLVED_CONTROL;

    const posthog = new PostHog();
    if (!posthog.active()) return RESOLVED_CONTROL;

    // posthog-js replays flags cached from an earlier page load synchronously,
    // so returning borrowers resolve without ever seeing the gate
    const variant = posthog.getFeatureFlag(flag);
    return variant === null ? PENDING : { variant, resolved: true };
  });

  // Resolution is one-shot per flag: by the time a variant lands the caller
  // has acted on it, either redirecting or presenting control for the
  // borrower to choose from, so a flag arriving afterwards must not re-decide
  // the page under them [sc-26315 / PR 181]
  const decided = useRef(state.resolved ? flag : undefined);

  useEffect(() => {
    if (decided.current === flag) return undefined;

    const resolve = (variant) => {
      if (decided.current === flag) return;

      decided.current = flag;
      setState(
        variant === null ? RESOLVED_CONTROL : { variant, resolved: true }
      );
    };

    // Drops a decision carried over from a previously evaluated flag
    setState(PENDING);

    if (!flag) {
      resolve(null);
      return undefined;
    }

    const posthog = new PostHog();
    if (!posthog.active()) {
      resolve(null);
      return undefined;
    }

    const unsubscribe = posthog.onFeatureFlags(() => {
      if (decided.current === flag) return;

      resolve(posthog.getFeatureFlag(flag));
    });

    // Nothing will ever arrive without a live client
    if (typeof unsubscribe !== 'function') {
      resolve(null);
      return undefined;
    }

    const timeout = setTimeout(() => resolve(null), FLAG_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [flag]);

  return state;
}
